import { useEffect, useState } from "react";
import { LockedAutocomplete, type AutocompleteOption } from "@/components/LockedAutocomplete";
import { Dialog } from "@/components/ui/Dialog";
import { AutoResizeTextarea } from "@/components/ui/AutoResizeTextarea";
import { SelectInput, TextInput } from "@/components/ui/Field";
import { BestellnummerField } from "@/components/BestellnummerField";
import { LEITWEG_ID_HELP } from "@/lib/leitwegId";
import { GdRichTextInput } from "@/components/ui/GdRichTextInput";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  RECHNUNG_ART_LABELS,
  SELECTABLE_RECHNUNG_ART_OPTIONS,
  SELECTABLE_RECHNUNG_ARTEN,
  applyGeschaeftspartnerQuelle,
  applyRechnungsadresseToEmpfaenger,
  effectiveBelegdatum,
  formatReferenzdokumentLabel,
  getBueroAnsprechpartnerKontakt,
  getGeschaeftspartnerQuelle,
  listBueroAnsprechpartner,
  type BueroAnsprechpartner,
  type DokumentDetail,
  type DokumentRechnungsadresse,
  type RechnungArt,
} from "@/lib/dokumente";
import {
  listGeschaeftspartnerAnsprechpartner,
  type Geschaeftspartner,
  type KontaktAnsprechpartner,
} from "@/lib/kontakte";
import { loadAppKundeOptions } from "@/pages/dokumente/kundeAutocomplete";

function formatRechnungsadresseOption(addr: DokumentRechnungsadresse): string {
  const street = [addr.strasse, addr.hausnummer].filter(Boolean).join(" ").trim();
  const city = [addr.plz, addr.ort].filter(Boolean).join(" ").trim();
  const parts = [
    addr.bezeichnung,
    addr.firma,
    addr.adresszusatz,
    street,
    city,
    addr.is_default ? "(Standard)" : null,
  ].filter(Boolean);
  return parts.join(" · ") || `Adresse #${addr.id}`;
}

export type HeaderEditSection = "empfaenger" | "betreff" | "ersteller_kontakt";

const SECTION_TITLES: Record<HeaderEditSection, string> = {
  empfaenger: "Empfänger",
  betreff: "Betreff & Termine",
  ersteller_kontakt: "Ansprechpartner (Ersteller)",
};

interface DocumentHeaderModalProps {
  open: boolean;
  section: HeaderEditSection | null;
  onClose: () => void;
  doc: DokumentDetail;
  editable: boolean;
  onChange: (patch: Partial<DokumentDetail>) => void;
  onResetCreatorContact?: () => void;
  onEditReferenzen?: () => void;
  zIndexClass?: string;
}

function bueroOptionFromMember(
  member: BueroAnsprechpartner,
): AutocompleteOption<BueroAnsprechpartner> {
  return {
    key: member.user_login.toLowerCase(),
    label: member.display_name || member.user_login,
    description: member.user_login,
    value: member,
  };
}

function ansprechpartnerLabel(ap: KontaktAnsprechpartner): string {
  const name = [ap.anrede, ap.vorname, ap.nachname].filter(Boolean).join(" ").trim();
  return name || ap.mail || `Ansprechpartner #${ap.id}`;
}

function ansprechpartnerOptionFromKontakt(
  ap: KontaktAnsprechpartner,
): AutocompleteOption<KontaktAnsprechpartner> {
  return {
    key: String(ap.id),
    label: ansprechpartnerLabel(ap),
    description: ap.mail || ap.telefonnummer || undefined,
    value: ap,
  };
}

export function DocumentHeaderModal({
  open,
  section,
  onClose,
  doc,
  editable,
  onChange,
  onResetCreatorContact,
  onEditReferenzen,
  zIndexClass,
}: DocumentHeaderModalProps) {
  const activeSection = section ?? "betreff";
  const referenzdokumentLabel = formatReferenzdokumentLabel(doc);
  const zeitraumVon = (doc.leistungszeitraum_von || "").slice(0, 10);
  const zeitraumBis = (doc.leistungszeitraum_bis || "").slice(0, 10);
  const hatLeistungszeitraum = Boolean(zeitraumVon && zeitraumBis && zeitraumVon !== zeitraumBis);
  const [bueroOption, setBueroOption] = useState<AutocompleteOption<BueroAnsprechpartner> | null>(
    null,
  );
  const [bueroLoading, setBueroLoading] = useState(false);
  const [bueroError, setBueroError] = useState<string | null>(null);
  const [kundeOption, setKundeOption] = useState<AutocompleteOption<Geschaeftspartner> | null>(null);
  const [kundeLoading, setKundeLoading] = useState(false);
  const [kundeError, setKundeError] = useState<string | null>(null);
  const [apOption, setApOption] = useState<AutocompleteOption<KontaktAnsprechpartner> | null>(null);

  useEffect(() => {
    if (!open || activeSection !== "ersteller_kontakt") {
      setBueroOption(null);
      setBueroLoading(false);
      setBueroError(null);
    }
  }, [open, activeSection]);

  useEffect(() => {
    if (!open || activeSection !== "empfaenger") {
      setKundeOption(null);
      setKundeLoading(false);
      setKundeError(null);
      setApOption(null);
    }
  }, [open, activeSection]);

  async function handleKundeSelect(next: AutocompleteOption<Geschaeftspartner> | null) {
    setKundeOption(next);
    setKundeError(null);
    setApOption(null);
    if (!next || !editable) return;

    setKundeLoading(true);
    try {
      const quelle = await getGeschaeftspartnerQuelle(next.value.id);
      onChange(applyGeschaeftspartnerQuelle(quelle, { dokumentTyp: doc.dokument_typ }));
    } catch (err) {
      setKundeError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Kunde konnte nicht übernommen werden.",
      );
    } finally {
      setKundeLoading(false);
    }
  }

  function handleAnsprechpartnerSelect(next: AutocompleteOption<KontaktAnsprechpartner> | null) {
    setApOption(next);
    if (!next || !editable) return;
    onChange({
      ansprechpartner_id: next.value.id,
      ansprechpartner_name: ansprechpartnerLabel(next.value),
      ansprechpartner_email: next.value.mail ?? "",
      ansprechpartner_telefon: next.value.telefonnummer ?? "",
    });
  }

  async function handleBueroSelect(next: AutocompleteOption<BueroAnsprechpartner> | null) {
    setBueroOption(next);
    setBueroError(null);
    if (!next || !editable) return;

    setBueroLoading(true);
    try {
      const kontakt = await getBueroAnsprechpartnerKontakt(next.value.user_login);
      onChange({
        created_by_name: kontakt.created_by_name ?? kontakt.display_name ?? "",
        created_by_email: kontakt.created_by_email ?? "",
        created_by_telefon: kontakt.created_by_telefon ?? "",
        created_by_mobil: kontakt.created_by_mobil ?? "",
      });
    } catch (err) {
      setBueroError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Kontaktdaten konnten nicht geladen werden.",
      );
    } finally {
      setBueroLoading(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={SECTION_TITLES[activeSection]}
      size="lg"
      zIndexClass={zIndexClass}
    >
      {activeSection === "empfaenger" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {editable ? (
            <div className="space-y-2 sm:col-span-2">
              <LockedAutocomplete<Geschaeftspartner>
                label="Kunde aus der App"
                placeholder="Kunden suchen, die in der App angelegt sind…"
                emptyText="Kein Kunde gefunden"
                value={kundeOption}
                onChange={(next) => void handleKundeSelect(next)}
                minChars={0}
                disabled={kundeLoading}
                loadOptions={loadAppKundeOptions}
                hint="Übernimmt Name, Adresse, Kundennummer und Kontaktdaten aus den Kontakten."
              />
              {kundeLoading ? (
                <div className="flex items-center gap-2 text-sm text-label-secondary">
                  <Spinner className="h-4 w-4" />
                  Kundendaten werden übernommen …
                </div>
              ) : null}
              {kundeError ? <p className="text-sm text-status-red">{kundeError}</p> : null}
              <LockedAutocomplete<KontaktAnsprechpartner>
                label="Ansprechpartner aus der App"
                placeholder={
                  kundeOption
                    ? "Ansprechpartner suchen…"
                    : "Bitte zuerst einen Kunden aus der App wählen"
                }
                emptyText="Kein Ansprechpartner gefunden"
                value={apOption}
                onChange={handleAnsprechpartnerSelect}
                minChars={0}
                disabled={!kundeOption || kundeLoading}
                disabledHint={!kundeOption ? "Wählen Sie zuerst einen Kunden aus der App." : null}
                loadOptions={async (query) => {
                  if (!kundeOption) return [];
                  const rows = await listGeschaeftspartnerAnsprechpartner(kundeOption.value.id);
                  const q = query.trim().toLowerCase();
                  return rows
                    .map(ansprechpartnerOptionFromKontakt)
                    .filter((opt) => !q || opt.label.toLowerCase().includes(q));
                }}
              />
            </div>
          ) : null}
          {doc.dokument_typ === "rechnung" ? (
            (doc.rechnungsadressen?.length ?? 0) > 0 ? (
              <SelectInput
                label="Rechnungsadresse"
                className="sm:col-span-2"
                value={doc.rechnungsadresse_id != null ? String(doc.rechnungsadresse_id) : ""}
                disabled={!editable}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (!raw) return;
                  const addrId = Number(raw);
                  const addr = (doc.rechnungsadressen || []).find((a) => a.id === addrId);
                  if (!addr) {
                    onChange({ rechnungsadresse_id: addrId });
                    return;
                  }
                  onChange(applyRechnungsadresseToEmpfaenger(addr));
                }}
              >
                {(doc.rechnungsadressen || []).map((addr) => (
                  <option key={addr.id} value={addr.id}>
                    {formatRechnungsadresseOption(addr)}
                  </option>
                ))}
              </SelectInput>
            ) : (
              <p className="sm:col-span-2 text-sm text-label-secondary">
                Keine Rechnungsadressen beim Kunden hinterlegt. Bitte unter Kontakte → Rechnung
                anlegen.
              </p>
            )
          ) : null}
          <TextInput
            label="Kunde"
            value={doc.kunde_name || ""}
            onChange={(e) => onChange({ kunde_name: e.target.value })}
            disabled={!editable}
          />
          <TextInput
            label="Kundennummer"
            value={doc.kundennummer || ""}
            onChange={(e) => onChange({ kundennummer: e.target.value || null })}
            disabled={!editable}
            placeholder="Hero-Kundennummer"
          />
          <TextInput
            label="Ansprechpartner (Kunde)"
            value={doc.ansprechpartner_name || ""}
            onChange={(e) => onChange({ ansprechpartner_name: e.target.value })}
            disabled={!editable}
          />
          <TextInput
            label="Adresszusatz"
            className="sm:col-span-2"
            value={doc.kunde_adresszusatz || ""}
            onChange={(e) => onChange({ kunde_adresszusatz: e.target.value })}
            disabled={!editable}
            placeholder="z. B. H12 Accounts Payable"
          />
          <TextInput
            label="Straße"
            value={doc.kunde_strasse || ""}
            onChange={(e) => onChange({ kunde_strasse: e.target.value })}
            disabled={!editable}
          />
          <TextInput
            label="Hausnummer"
            value={doc.kunde_hausnummer || ""}
            onChange={(e) => onChange({ kunde_hausnummer: e.target.value })}
            disabled={!editable}
          />
          <TextInput
            label="PLZ"
            value={doc.kunde_plz || ""}
            onChange={(e) => onChange({ kunde_plz: e.target.value })}
            disabled={!editable}
          />
          <TextInput
            label="Ort"
            value={doc.kunde_ort || ""}
            onChange={(e) => onChange({ kunde_ort: e.target.value })}
            disabled={!editable}
          />
          <TextInput
            label="E-Mail (Kunde)"
            value={doc.kunde_email ?? ""}
            onChange={(e) => onChange({ kunde_email: e.target.value })}
            disabled={!editable}
          />
          <TextInput
            label="E-Mail (Ansprechpartner)"
            value={doc.ansprechpartner_email ?? ""}
            onChange={(e) => onChange({ ansprechpartner_email: e.target.value })}
            disabled={!editable}
          />
          <TextInput
            label="Telefon (Kunde)"
            value={doc.kunde_telefon ?? ""}
            onChange={(e) => onChange({ kunde_telefon: e.target.value })}
            disabled={!editable}
          />
          <TextInput
            label="Telefon (Ansprechpartner)"
            value={doc.ansprechpartner_telefon ?? ""}
            onChange={(e) => onChange({ ansprechpartner_telefon: e.target.value })}
            disabled={!editable}
          />
        </div>
      ) : null}

      {activeSection === "betreff" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {doc.dokument_typ === "rechnung" ? (
            <label className="block space-y-1 text-sm sm:col-span-2">
              <span className="text-label-secondary">Rechnungsart</span>
              <select
                value={doc.rechnung_art || ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  onChange({
                    rechnung_art: raw ? (raw as RechnungArt) : null,
                  });
                }}
                disabled={!editable}
                className="eh-input w-full"
              >
                {(
                  doc.rechnung_art === "stornorechnung"
                    ? [
                        { value: "stornorechnung" as const, label: RECHNUNG_ART_LABELS.stornorechnung },
                        ...SELECTABLE_RECHNUNG_ART_OPTIONS,
                      ]
                    : doc.rechnung_art &&
                        !SELECTABLE_RECHNUNG_ARTEN.includes(doc.rechnung_art)
                      ? [
                          {
                            value: doc.rechnung_art,
                            label: RECHNUNG_ART_LABELS[doc.rechnung_art] || doc.rechnung_art,
                          },
                          ...SELECTABLE_RECHNUNG_ART_OPTIONS,
                        ]
                      : SELECTABLE_RECHNUNG_ART_OPTIONS
                ).map((opt) => (
                  <option key={opt.value || "rechnung"} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <span className="block text-xs text-label-tertiary">
                Automatisch: erste Rechnung als Rechnung, danach Teilrechnung bei offenem
                Projekt, Schlussrechnung nach Projektabschluss.
              </span>
            </label>
          ) : null}
          {doc.dokument_typ === "rechnung" ? (
            <label className="flex items-start gap-2 text-sm sm:col-span-2">
              <input
                type="checkbox"
                checked={Boolean(doc.reverse_charge_13b)}
                onChange={(e) => onChange({ reverse_charge_13b: e.target.checked })}
                disabled={!editable}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium text-label">
                  § 13b UStG – Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge)
                </span>
                <span className="block text-xs text-label-tertiary">
                  Setzt alle Positionen auf 0 % USt und ergänzt den Pflichthinweis auf PDF und
                  E-Rechnung. Die USt-IdNr. des Kunden (Pflichtangabe) wird aus den
                  Rechnungseinstellungen des Kunden übernommen. Beim Deaktivieren bleiben die
                  Steuersätze auf 0 % und müssen manuell angepasst werden.
                </span>
              </span>
            </label>
          ) : null}
          <label className="block space-y-1 text-sm sm:col-span-2">
            <span className="text-label-secondary">Betreff</span>
            <GdRichTextInput
              value={doc.betreff || ""}
              onChange={(e) => onChange({ betreff: e.target.value })}
              disabled={!editable}
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-label-secondary">Belegdatum</span>
            <input
              type="date"
              value={(effectiveBelegdatum(doc) || "").slice(0, 10)}
              onChange={(e) => onChange({ belegdatum: e.target.value || null })}
              disabled={!editable || doc.status === "entwurf"}
              className="eh-input w-full"
            />
            {doc.status === "entwurf" ? (
              <span className="block text-xs text-label-tertiary">
                Bei Entwürfen immer das heutige Datum (wird beim Erstellen festgeschrieben).
              </span>
            ) : null}
          </label>
          {doc.dokument_typ === "angebot" ? (
            <label className="block space-y-1 text-sm">
              <span className="text-label-secondary">Gültig bis</span>
              <input
                type="date"
                value={(doc.gueltig_bis || "").slice(0, 10)}
                onChange={(e) => onChange({ gueltig_bis: e.target.value || null })}
                disabled={!editable}
                className="eh-input w-full"
              />
            </label>
          ) : null}
          <div className="sm:col-span-2">
            <BestellnummerField
              id="gd-bestellnummer"
              value={doc.bestellnummer || ""}
              onChange={(next) => onChange({ bestellnummer: next || null })}
              disabled={!editable}
              quelle={doc.auftrag_bestellnummer_quelle}
              regelWert={doc.auftrag_bestellnummer_regel_wert}
              placeholder="Kunden-Bestellnummer"
              onApplyRegel={
                doc.auftrag_bestellnummer_regel_wert
                  ? () =>
                      onChange({
                        bestellnummer: doc.auftrag_bestellnummer_regel_wert || null,
                      })
                  : undefined
              }
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={doc.referenzdokument_anzeigen !== false}
                onChange={(e) => onChange({ referenzdokument_anzeigen: e.target.checked })}
                disabled={!editable}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium text-label">
                  Referenzdokumente im Dokument anzeigen
                </span>
                <span className="block text-xs text-label-tertiary">
                  {referenzdokumentLabel
                    ? `Zeigt „Referenzdokument: ${referenzdokumentLabel}“ im Infoblock. Mehrere Dokumente (z. B. Angebot und Lieferschein) sind möglich.`
                    : "Noch keine Referenz gewählt. Über „Auswählen“ können Angebot, Lieferschein oder weitere Dokumente des Projekts gesetzt werden."}
                </span>
              </span>
            </label>
            {onEditReferenzen ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={onEditReferenzen}
                disabled={!editable && !referenzdokumentLabel}
              >
                Referenzdokumente auswählen
              </Button>
            ) : null}
          </div>
          {doc.auftrag_id ? (
            <label className="block space-y-1 text-sm sm:col-span-2">
              <span className="text-label-secondary">Kundenreferenz</span>
              <input
                type="text"
                value={doc.auftrag_kundenreferenz || ""}
                onChange={(e) =>
                  onChange({ auftrag_kundenreferenz: e.target.value || null })
                }
                disabled={!editable}
                className="eh-input w-full"
                placeholder="Kundenreferenz"
              />
            </label>
          ) : null}
          <label className="block space-y-1 text-sm sm:col-span-2">
            <span className="text-label-secondary">Leitweg-ID</span>
            <input
              type="text"
              value={doc.leitweg_id || ""}
              onChange={(e) => onChange({ leitweg_id: e.target.value || null })}
              disabled={!editable}
              className="eh-input w-full font-mono"
              placeholder="z. B. 04011000-1234512345-06"
              spellCheck={false}
            />
            <span className="block text-xs text-label-tertiary">{LEITWEG_ID_HELP}</span>
            {(doc.leitweg_id || "").trim() && (doc.auftrag_kundenreferenz || "").trim() ? (
              <span className="block text-xs text-label-tertiary">
                Als BT-10 wird die Leitweg-ID übertragen. Die Kundenreferenz bleibt separat
                gespeichert.
              </span>
            ) : null}
            {editable && !(doc.leitweg_id || "").trim() && doc.leitweg_id_vorschlag ? (
              <button
                type="button"
                className="text-xs text-accent"
                onClick={() => onChange({ leitweg_id: doc.leitweg_id_vorschlag || null })}
              >
                Vorschlag übernehmen: {doc.leitweg_id_vorschlag}
              </button>
            ) : null}
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-label-secondary">Leistungszeitraum von</span>
            <input
              type="date"
              value={zeitraumVon}
              onChange={(e) => onChange({ leistungszeitraum_von: e.target.value || null })}
              disabled={!editable}
              className="eh-input w-full"
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-label-secondary">Leistungszeitraum bis</span>
            <input
              type="date"
              value={zeitraumBis}
              onChange={(e) => {
                const bis = e.target.value || null;
                onChange({
                  leistungszeitraum_bis: bis,
                  ...(bis ? { leistungsdatum: bis } : {}),
                });
              }}
              disabled={!editable}
              className="eh-input w-full"
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-label-secondary">Leistungsdatum</span>
            <input
              type="date"
              value={(doc.leistungsdatum || zeitraumBis || "").slice(0, 10)}
              onChange={(e) => onChange({ leistungsdatum: e.target.value || null })}
              disabled={!editable || hatLeistungszeitraum}
              className="eh-input w-full"
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-label-secondary">Lieferdatum</span>
            <input
              type="date"
              value={(doc.lieferdatum || "").slice(0, 10)}
              onChange={(e) => onChange({ lieferdatum: e.target.value || null })}
              disabled={!editable}
              className="eh-input w-full"
            />
          </label>
          <p className="text-xs text-label-tertiary sm:col-span-2">
            Für die Umsatzsteuer zählt das Ende der Leistung bzw. das Lieferdatum. Ein Zeitraum
            (z. B. aus dem Stundennachweis) steht als Leistungszeitraum auf der Rechnung. Deckt sich
            das Datum mit dem Rechnungsdatum, reicht der Vermerk „Leistungsdatum entspricht
            Rechnungsdatum“.
          </p>
          <label className="block space-y-1 text-sm sm:col-span-2">
            <span className="text-label-secondary">Interne Notiz</span>
            <AutoResizeTextarea
              value={doc.interne_notiz || ""}
              onChange={(e) => onChange({ interne_notiz: e.target.value })}
              disabled={!editable}
              minRows={2}
            />
          </label>
          {doc.auftrag_nummer ? (
            <p className="text-sm text-label-secondary sm:col-span-2">
              Projekt: {doc.auftrag_nummer}
              {doc.auftrag_ort ? ` · ${doc.auftrag_ort}` : ""}
            </p>
          ) : null}
        </div>
      ) : null}

      {activeSection === "ersteller_kontakt" ? (
        <div className="space-y-4">
          <p className="text-sm text-label-secondary">
            Diese Angaben erscheinen im Infoblock des Dokuments. Wählen Sie einen Büro-Mitarbeiter
            oder überschreiben Sie die Kontaktdaten manuell.
          </p>
          {editable ? (
            <div className="space-y-2">
              <LockedAutocomplete<BueroAnsprechpartner>
                label="Mitarbeiter suchen (Gruppe Büro)"
                placeholder="Name oder Login suchen…"
                emptyText="Kein Büro-Mitarbeiter gefunden"
                value={bueroOption}
                onChange={(next) => void handleBueroSelect(next)}
                minChars={0}
                disabled={bueroLoading}
                loadOptions={async (query) => {
                  const rows = await listBueroAnsprechpartner(query, 200);
                  return rows.map(bueroOptionFromMember);
                }}
                hint="Übernimmt Name, Telefon, Mobil und E-Mail in die Felder darunter."
              />
              {bueroLoading ? (
                <div className="flex items-center gap-2 text-sm text-label-secondary">
                  <Spinner className="h-4 w-4" />
                  Kontaktdaten werden geladen …
                </div>
              ) : null}
              {bueroError ? <p className="text-sm text-status-red">{bueroError}</p> : null}
            </div>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <TextInput
              label="Name"
              value={doc.created_by_name || ""}
              onChange={(e) => onChange({ created_by_name: e.target.value })}
              disabled={!editable}
              className="sm:col-span-2"
            />
            <TextInput
              label="Telefon"
              value={doc.created_by_telefon ?? ""}
              onChange={(e) => onChange({ created_by_telefon: e.target.value })}
              disabled={!editable}
            />
            <TextInput
              label="Mobil"
              value={doc.created_by_mobil ?? ""}
              onChange={(e) => onChange({ created_by_mobil: e.target.value })}
              disabled={!editable}
            />
            <TextInput
              label="E-Mail"
              value={doc.created_by_email ?? ""}
              onChange={(e) => onChange({ created_by_email: e.target.value })}
              disabled={!editable}
              className="sm:col-span-2"
            />
          </div>
          {editable && onResetCreatorContact ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                setBueroOption(null);
                setBueroError(null);
                onResetCreatorContact();
              }}
            >
              Standard wiederherstellen
            </Button>
          ) : null}
        </div>
      ) : null}

    </Dialog>
  );
}
